import { useState, useEffect } from 'react';
import { useLosses, useProducts, useMenu, useDishes } from '../../hooks/useData';
import { DataTable, Badge } from '../../components/shared/KPIComponents';
import { api, getCurrentMonth, formatCurrency, LOSS_REASONS } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Trash2, Plus, AlertTriangle, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmDelete from '../../components/ConfirmDelete';

const ChefPertes = () => {
  const currentMonth = getCurrentMonth();
  const today = new Date().toISOString().split('T')[0];
  
  const { losses, loading, refetch } = useLosses(currentMonth);
  const { products } = useProducts();
  const { menu } = useMenu();
  const { dishes } = useDishes();
  
  const [showForm, setShowForm] = useState(false);
  const [selectedLosses, setSelectedLosses] = useState({});
  const [editingLoss, setEditingLoss] = useState(null);
  const [editForm, setEditForm] = useState({});
  
  // Get available dishes for today (from localStorage set by patron)
  const [availableDishes, setAvailableDishes] = useState([]);
  
  useEffect(() => {
    const stored = localStorage.getItem('lemas_available_dishes');
    if (stored) {
      setAvailableDishes(JSON.parse(stored));
    }
  }, []);
  
  // Menu dishes
  const menuDishes = menu?.plats?.map(p => p.nom) || [];
  
  // All dishes to show for losses
  const allLossDishes = [...new Set([...menuDishes, ...availableDishes])];
  
  const findProductPrice = (dishName) => {
    // Try to find matching product from invoices
    const product = products.find(p => 
      p.nom.toLowerCase().includes(dishName.toLowerCase()) ||
      dishName.toLowerCase().includes(p.nom.toLowerCase())
    );
    return product?.prix_unitaire || 0;
  };
  
  const handleLossChange = (dishName, field, value) => {
    setSelectedLosses(prev => ({
      ...prev,
      [dishName]: {
        ...prev[dishName],
        [field]: value,
        prix_unitaire: prev[dishName]?.prix_unitaire || findProductPrice(dishName)
      }
    }));
  };
  
  const handleSubmitLosses = async () => {
    const lossesToSubmit = Object.entries(selectedLosses)
      .filter(([_, data]) => data.quantite > 0)
      .map(([produit, data]) => ({
        date: today,
        produit,
        quantite: parseFloat(data.quantite) || 0,
        unite: data.unite || 'kg',
        prix_unitaire: parseFloat(data.prix_unitaire) || 0,
        raison: data.raison || 'Autre'
      }));
    
    if (lossesToSubmit.length === 0) {
      toast.error('Aucune perte à enregistrer');
      return;
    }
    
    try {
      for (const loss of lossesToSubmit) {
        await api.post('/losses', loss);
      }
      
      const total = lossesToSubmit.reduce((sum, l) => sum + (l.quantite * l.prix_unitaire), 0);
      toast.success(`${lossesToSubmit.length} pertes enregistrées (${formatCurrency(total)})`);
      setSelectedLosses({});
      refetch();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };
  
  const handleDeleteLoss = async (id) => {
    try {
      await api.delete(`/losses/${id}`);
      toast.success('Perte supprimée');
      refetch();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  // Today's losses
  const todayLosses = losses.filter(l => l.date === today);
  const todayTotal = todayLosses.reduce((sum, l) => sum + l.montant, 0);
  
  // Month's losses
  const monthTotal = losses.reduce((sum, l) => sum + l.montant, 0);

  const startEditLoss = (loss) => {
    setEditingLoss(loss.id);
    setEditForm({
      produit: loss.produit,
      quantite: loss.quantite,
      unite: loss.unite,
      prix_unitaire: loss.prix_unitaire,
      raison: loss.raison || '',
    });
  };

  const saveEditLoss = async () => {
    try {
      await api.put(`/losses/${editingLoss}`, {
        produit: editForm.produit,
        quantite: parseFloat(editForm.quantite) || 0,
        unite: editForm.unite,
        prix_unitaire: parseFloat(editForm.prix_unitaire) || 0,
        raison: editForm.raison,
      });
      toast.success('Perte modifiée');
      setEditingLoss(null);
      refetch();
    } catch { toast.error('Erreur'); }
  };
  
  // Calculate total from selected losses
  const selectedTotal = Object.entries(selectedLosses)
    .filter(([_, data]) => data.quantite > 0)
    .reduce((sum, [_, data]) => sum + ((parseFloat(data.quantite) || 0) * (parseFloat(data.prix_unitaire) || 0)), 0);
  
  const columns = [
    { header: 'Date', render: (row) => <span className="font-mono text-sm">{row.date}</span> },
    { header: 'Produit', render: (row) => <span className="font-medium">{row.produit}</span> },
    { header: 'Quantité', render: (row) => <span className="font-mono">{row.quantite} {row.unite}</span> },
    { header: 'Prix unit.', render: (row) => <span className="font-mono">{formatCurrency(row.prix_unitaire)}/{row.unite}</span> },
    { header: 'Montant', render: (row) => <span className="font-mono text-lemas-danger">{formatCurrency(row.montant)}</span> },
    { header: 'Raison', render: (row) => <Badge type="default">{row.raison}</Badge> },
    { header: '', render: (row) => (
      <ConfirmDelete onConfirm={() => handleDeleteLoss(row.id)}>
        <Button variant="ghost" size="sm" className="text-lemas-danger"><Trash2 size={14} /></Button>
      </ConfirmDelete>
    )},
  ];
  
  return (
    <div className="space-y-6" data-testid="chef-pertes">
      <div className="flex items-center justify-between animate-slide-up">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Gestion</p>
          <h2 className="font-title text-2xl text-[rgb(var(--lm-text))] flex items-center gap-2">
            <Trash2 size={20} style={{ color: 'rgb(var(--lm-red))' }} />
            Pertes du soir
          </h2>
        </div>
      </div>
      
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="lm-card p-5 text-center animate-slide-up" style={{ animationDelay: '60ms', animationFillMode: 'backwards' }}>
          <p className="kpi-label">Aujourd'hui</p>
          <p className="font-mono text-2xl" style={{ color: 'rgb(var(--lm-red))' }}>{formatCurrency(todayTotal)}</p>
        </div>
        <div className="lm-card p-5 text-center animate-slide-up" style={{ animationDelay: '120ms', animationFillMode: 'backwards' }}>
          <p className="kpi-label">Ce mois</p>
          <p className="font-mono text-2xl text-[rgb(var(--lm-text))]">{formatCurrency(monthTotal)}</p>
        </div>
      </div>
      
      {/* Loss Entry Form */}
      <Button 
        onClick={() => setShowForm(!showForm)}
        className="w-full bg-lemas-green text-lemas-bg hover:bg-lemas-green/90"
        data-testid="open-loss-form"
      >
        <Plus size={18} className="mr-2" />
        Saisir les pertes du soir
      </Button>
      
      {showForm && (
        <div className="kpi-card space-y-6">
          {/* Menu du jour section */}
          {menuDishes.length > 0 && (
            <div>
              <h3 className="font-title text-lg text-lemas-green mb-3">Menu du jour</h3>
              <div className="space-y-3">
                {menuDishes.map((dish) => (
                  <LossEntryRow 
                    key={dish}
                    dishName={dish}
                    data={selectedLosses[dish] || {}}
                    onChange={(field, value) => handleLossChange(dish, field, value)}
                    suggestedPrice={findProductPrice(dish)}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Carte du jour section */}
          {availableDishes.length > 0 && (
            <div>
              <h3 className="font-title text-lg text-lemas-green mb-3">Carte du jour</h3>
              <div className="space-y-3">
                {availableDishes.filter(d => !menuDishes.includes(d)).map((dish) => (
                  <LossEntryRow 
                    key={dish}
                    dishName={dish}
                    data={selectedLosses[dish] || {}}
                    onChange={(field, value) => handleLossChange(dish, field, value)}
                    suggestedPrice={findProductPrice(dish)}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* No dishes warning */}
          {menuDishes.length === 0 && availableDishes.length === 0 && (
            <div className="text-center py-8 text-[rgb(var(--lm-text-secondary))]">
              <AlertTriangle className="mx-auto mb-2" size={32} />
              <p>Aucun plat disponible</p>
              <p className="text-sm">Le patron doit publier le menu ou marquer des plats disponibles</p>
            </div>
          )}
          
          {/* Total and Submit */}
          {selectedTotal > 0 && (
            <div className="pt-4 border-t border-[rgb(var(--lm-border))]">
              <div className="flex justify-between items-center mb-4">
                <span className="text-[rgb(var(--lm-text-secondary))]">Total pertes à enregistrer</span>
                <span className="font-mono text-2xl text-lemas-danger">{formatCurrency(selectedTotal)}</span>
              </div>
              <Button 
                onClick={handleSubmitLosses}
                className="w-full bg-lemas-danger text-white hover:bg-red-600"
                data-testid="submit-losses"
              >
                Enregistrer les pertes
              </Button>
            </div>
          )}
        </div>
      )}
      
      {/* Today's losses */}
      {todayLosses.length > 0 && (
        <div className="kpi-card">
          <h3 className="font-title text-lg mb-4">Pertes enregistrées aujourd'hui</h3>
          <div className="space-y-2">
            {todayLosses.map(loss => (
              editingLoss === loss.id ? (
                <div key={loss.id} className="p-3 rounded-lg bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-gold)/0.3)] space-y-2" data-testid={`edit-loss-${loss.id}`}>
                  <Input value={editForm.produit} onChange={e => setEditForm(f => ({ ...f, produit: e.target.value }))}
                    className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm" data-testid="edit-loss-produit" />
                  <div className="flex gap-2">
                    <Input type="number" value={editForm.quantite} onChange={e => setEditForm(f => ({ ...f, quantite: e.target.value }))}
                      className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono text-sm w-20" placeholder="Qté" />
                    <select value={editForm.unite} onChange={e => setEditForm(f => ({ ...f, unite: e.target.value }))}
                      className="bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md px-2 text-sm w-20">
                      <option value="kg">kg</option><option value="piece">pièce</option><option value="L">L</option>
                    </select>
                    <Input type="number" step="0.01" value={editForm.prix_unitaire} onChange={e => setEditForm(f => ({ ...f, prix_unitaire: e.target.value }))}
                      className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono text-sm w-24" placeholder="€/u" />
                    <select value={editForm.raison} onChange={e => setEditForm(f => ({ ...f, raison: e.target.value }))}
                      className="bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md px-2 text-sm flex-1">
                      <option value="">Raison...</option>
                      {LOSS_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setEditingLoss(null)} className="text-[rgb(var(--lm-text-muted))]">
                      <X size={14} className="mr-1" /> Annuler
                    </Button>
                    <Button size="sm" onClick={saveEditLoss} className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))]">
                      <Check size={14} className="mr-1" /> Enregistrer
                    </Button>
                  </div>
                </div>
              ) : (
                <div key={loss.id} className="flex justify-between items-center py-2 border-b border-[rgb(var(--lm-border))] cursor-pointer hover:bg-[rgb(var(--lm-surface-elevated)/0.3)] rounded px-1 transition-colors"
                  onClick={() => startEditLoss(loss)} data-testid={`loss-row-${loss.id}`}>
                  <div>
                    <p className="font-medium">{loss.produit}</p>
                    <p className="text-xs text-[rgb(var(--lm-text-secondary))]">{loss.quantite} {loss.unite} x {loss.prix_unitaire}€ - {loss.raison}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-lemas-danger">{formatCurrency(loss.montant)}</span>
                    <Pencil size={12} className="text-[rgb(var(--lm-text-muted))]" />
                    <ConfirmDelete onConfirm={() => handleDeleteLoss(loss.id)}>
                      <Button variant="ghost" size="sm" className="text-[rgb(var(--lm-text-muted))]"><Trash2 size={14} /></Button>
                    </ConfirmDelete>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      )}
      
      {/* History */}
      <div className="kpi-card">
        <h3 className="font-title text-lg mb-4">Historique des pertes</h3>
        <DataTable 
          columns={columns}
          data={losses.filter(l => l.date !== today)}
          emptyMessage="Aucune perte enregistrée"
        />
      </div>
    </div>
  );
};

// Loss entry row component
const LossEntryRow = ({ dishName, data, onChange, suggestedPrice }) => {
  return (
    <div className="flex flex-wrap items-center gap-2 p-3 bg-lemas-bg/50 rounded-lg">
      <span className="flex-1 min-w-[120px] font-medium">{dishName}</span>
      <Input 
        type="number"
        placeholder="Qté"
        value={data.quantite || ''}
        onChange={(e) => onChange('quantite', e.target.value)}
        className="w-16 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono text-center"
      />
      <select 
        value={data.unite || 'kg'}
        onChange={(e) => onChange('unite', e.target.value)}
        className="w-16 bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md p-2 text-sm"
      >
        <option value="kg">kg</option>
        <option value="piece">pièce</option>
        <option value="L">L</option>
      </select>
      <Input 
        type="number"
        placeholder="€/unité"
        value={data.prix_unitaire || suggestedPrice || ''}
        onChange={(e) => onChange('prix_unitaire', e.target.value)}
        className="w-20 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono text-center"
      />
      <select 
        value={data.raison || ''}
        onChange={(e) => onChange('raison', e.target.value)}
        className="w-32 bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md p-2 text-sm"
      >
        <option value="">Raison...</option>
        {LOSS_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
      </select>
    </div>
  );
};

export default ChefPertes;
